/**
 * Main Layout Component
 *
 * This component provides the main application layout structure:
 * - Header with navigation and user menu
 * - Collapsible sidebar with navigation links
 * - Main content area
 *
 * Uses React Router's Outlet to render child routes.
 */

import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PageContextProvider } from '../../context/PageContext';
import Button from '../common/Button';
import Breadcrumb from '../common/Breadcrumb';
import ChatWidget from '../chat/ChatWidget';
import * as fileApi from '../../api/fileApi';

/**
 * Navigation items for the sidebar
 * Each item has a path and label
 */
const navItems = [
  { path: '/document-queue', label: 'Document Queue', icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' },
  { path: '/purchase-orders', label: 'Purchase Orders', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { path: '/ocean-bookings', label: 'Ocean Bookings', icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z' },
  { path: '/bills-of-lading', label: 'Bills of Lading', icon: 'M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2' }
];

/**
 * Super admin navigation items (no company context)
 */
const superAdminNavItems = [
  { path: '/system-admin', label: 'System Admin', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' },
  { path: '/companies', label: 'Companies', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
  { path: '/admin/audit-log', label: 'Audit Log', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' }
];

/**
 * Admin-only navigation items
 */
const adminNavItems = [
  { path: '/admin/partners', label: 'Partners', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
  { path: '/settings', label: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' }
];

/**
 * MainLayout Component
 *
 * Renders the main application shell with header, sidebar, and content area.
 */
function MainLayout() {
  // Get current route location for active link highlighting
  const location = useLocation();
  const navigate = useNavigate();

  // Get auth context for user info and logout
  const { user, company, logout } = useAuth();

  // Sidebar collapsed state - persisted in localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Logo signed URL state
  const [logoSignedUrl, setLogoSignedUrl] = useState(null);

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed);
  }, [isCollapsed]);

  // Build proxy URL for logo when company has logoObjectKey
  useEffect(() => {
    if (!company || !company.logoObjectKey) {
      setLogoSignedUrl(null);
      return;
    }
    setLogoSignedUrl(fileApi.buildProxyUrl(company.logoObjectKey));
  }, [company]);

  /**
   * Toggle sidebar collapsed state
   */
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  /**
   * Handle user logout
   * Clears auth state and redirects to login
   */
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Sidebar width classes
  const sidebarWidth = isCollapsed ? 'w-16' : 'w-64';
  const mainMargin = isCollapsed ? 'ml-16' : 'ml-64';

  return (
    <PageContextProvider>
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between h-16 px-4">
          {/* Logo / App Name */}
          <Link to={user?.role === 'super_admin' ? '/system-admin' : '/purchase-orders'} className="flex items-center space-x-2">
            {logoSignedUrl ? (
              <img
                src={logoSignedUrl}
                alt={company?.name || 'Company logo'}
                className="h-10 w-auto object-contain"
              />
            ) : (
              <>
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">OMS</span>
                </div>
                <span className="text-xl font-semibold text-gray-900">
                  Order Management
                </span>
              </>
            )}
          </Link>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* Company and User Info */}
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">{company?.name}</p>
            </div>

            {/* Logout Button */}
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Container with Sidebar */}
      <div className="flex pt-16">
        {/* Sidebar Navigation */}
        <aside
          className={`
            ${sidebarWidth} bg-white border-r border-gray-200 min-h-[calc(100vh-4rem)] fixed left-0 top-16
            transition-all duration-300 ease-in-out
          `}
        >
          {/* Collapse Toggle Button */}
          <button
            onClick={toggleSidebar}
            className="absolute -right-3 top-6 w-6 h-6 bg-white border border-gray-200 rounded-full shadow-sm flex items-center justify-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 z-10"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <nav className={`p-4 space-y-1 ${isCollapsed ? 'px-2' : ''}`}>
            {(user?.role === 'super_admin' ? superAdminNavItems : navItems).map((item) => {
              // Check if this nav item is active
              const isActive = location.pathname === item.path ||
                (item.path !== '/' && location.pathname.startsWith(item.path));

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={isCollapsed ? item.label : undefined}
                  className={`
                    flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-3 py-2 rounded-md text-sm font-medium
                    transition-colors duration-150
                    ${isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  {/* Icon */}
                  <svg
                    className="w-5 h-5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={item.icon}
                    />
                  </svg>
                  {!isCollapsed && <span>{item.label}</span>}
                </Link>
              );
            })}

            {/* Admin-only nav items (not shown for super admin — they have no company) */}
            {user?.role === 'admin' && (
              <>
                <div className={`pt-4 mt-4 border-t border-gray-200 ${isCollapsed ? 'mx-1' : ''}`}>
                  {!isCollapsed && (
                    <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Admin
                    </p>
                  )}
                </div>
                {adminNavItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      title={isCollapsed ? item.label : undefined}
                      className={`
                        flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-3 py-2 rounded-md text-sm font-medium
                        transition-colors duration-150
                        ${isActive
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                    >
                      <svg
                        className="w-5 h-5 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={item.icon}
                        />
                      </svg>
                      {!isCollapsed && <span>{item.label}</span>}
                    </Link>
                  );
                })}
              </>
            )}
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className={`flex-1 ${mainMargin} p-6 transition-all duration-300 ease-in-out min-w-0`}>
          <Breadcrumb />
          <Outlet />
        </main>
      </div>

      {/* Chat widget — floating chatbot available on every authenticated page */}
      <ChatWidget />
    </div>
    </PageContextProvider>
  );
}

export default MainLayout;
